// The uploader tab's side of the channel (src/lib/uploader/uploaderTab.ts), mounted once at the app root:
//  - a tab with uploads reports them (light fields), on change (at most once a second), every few seconds
//    and whenever a launcher asks — so a gallery's Upload tab can show how its uploads are going;
//  - the named uploader tab follows "goto" (Open uploader clicked while it's already open) with an
//    in-app navigation, so the uploads running in it are never cut off by a reload.
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUploadManager } from "@/hooks/useUploadManager";
import { isUploadHost } from "@/lib/hostTenant";
import {
  TAB_ID,
  UPLOADER_PATH,
  isUploaderTab,
  lightJob,
  openUploaderChannel,
  post,
  uploaderUrl,
  type UploaderMessage,
} from "@/lib/uploader/uploaderTab";

const HEARTBEAT_MS = 5_000;

/** Where the uploader lives on this host (keeps whichever uploader URL the tab is already on). */
const uploaderPathHere = () => {
  const p = window.location.pathname.replace(/\/+$/, "") || "/";
  if (p === UPLOADER_PATH || p === "/upload") return p;
  return isUploadHost() ? "/" : UPLOADER_PATH;
};

export const UploaderTabBridge = () => {
  const { jobs } = useUploadManager();
  const navigate = useNavigate();
  const light = useMemo(() => jobs.map(lightJob), [jobs]);
  const lightRef = useRef(light);
  lightRef.current = light;
  const chRef = useRef<BroadcastChannel | null>(null);
  const lastSent = useRef(0);
  const reported = useRef(false);
  const timer = useRef<number | undefined>(undefined);

  const send = useCallback(() => {
    const uploader = isUploaderTab();
    const js = lightRef.current;
    // Quiet tabs stay quiet; the uploader tab always reports (so launchers know it's open), and a tab
    // whose uploads were all cleared says so once.
    if (!js.length && !uploader && !reported.current) return;
    post(chRef.current, { type: "status", from: TAB_ID, jobs: js, uploader, at: Date.now() });
    reported.current = js.length > 0 || uploader;
    lastSent.current = Date.now();
  }, []);

  useEffect(() => {
    const ch = openUploaderChannel();
    chRef.current = ch;
    if (!ch) return;
    const onMessage = (e: MessageEvent<UploaderMessage>) => {
      const m = e.data;
      if (!m || m.from === TAB_ID) return;
      if (m.type === "hello") send();
      else if (m.type === "goto" && isUploaderTab()) {
        navigate(uploaderUrl(m.target, uploaderPathHere()), { state: { goto: Date.now() } });
      }
    };
    const bye = () => {
      if (reported.current) post(ch, { type: "bye", from: TAB_ID });
    };
    ch.addEventListener("message", onMessage);
    window.addEventListener("pagehide", bye);
    const beat = window.setInterval(send, HEARTBEAT_MS);
    send();
    return () => {
      window.clearInterval(beat);
      window.clearTimeout(timer.current);
      window.removeEventListener("pagehide", bye);
      ch.removeEventListener("message", onMessage);
      ch.close();
      chRef.current = null;
    };
  }, [navigate, send]);

  // Report changes, at most once a second.
  useEffect(() => {
    window.clearTimeout(timer.current);
    const wait = 1000 - (Date.now() - lastSent.current);
    if (wait <= 0) send();
    else timer.current = window.setTimeout(send, wait);
  }, [light, send]);

  return null;
};

export default UploaderTabBridge;
